import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Inbox as InboxIcon,
  Mail,
  MailOpen,
  Paperclip,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useInboxList, type InboxEmailSummary } from '@/hooks/useInbox';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

type Tab = 'all' | 'unread' | 'archived';

export function PlatformInboxPage() {
  const [tab, setTab] = useState<Tab>('all');
  const list = useInboxList(tab);

  const totals = list.data?.totals ?? { total: 0, unread: 0, archived: 0 };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <InboxIcon className="w-5 h-5" /> Gelen Kutusu
        </h1>
        <p className="text-muted-foreground text-sm">
          Resend inbound webhook ile alınan e-postalar. Webhook URL'i ve key'ler{' '}
          <Link to="/platform/mailer" className="text-primary hover:underline">
            E-posta Ayarları
          </Link>
          {' '}sayfasında yapılandırılır.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b">
        <TabButton active={tab === 'all'} onClick={() => setTab('all')}>
          Tümü <span className="text-xs text-muted-foreground ml-1">({totals.total - totals.archived})</span>
        </TabButton>
        <TabButton active={tab === 'unread'} onClick={() => setTab('unread')}>
          Okunmamış{' '}
          {totals.unread > 0 && (
            <span className="text-xs rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 ml-1">
              {totals.unread}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === 'archived'} onClick={() => setTab('archived')}>
          Arşiv <span className="text-xs text-muted-foreground ml-1">({totals.archived})</span>
        </TabButton>
      </div>

      {list.isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : (list.data?.emails ?? []).length === 0 ? (
        <EmptyState tab={tab} />
      ) : (
        <div className="space-y-1.5">
          {(list.data?.emails ?? []).map((email) => (
            <EmailRow key={email.id} email={email} />
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState({ tab }: { tab: Tab }) {
  const msg =
    tab === 'unread'
      ? 'Tüm e-postalar okundu.'
      : tab === 'archived'
        ? 'Arşivde e-posta yok.'
        : 'Henüz e-posta gelmedi.';
  return (
    <Card>
      <CardContent className="py-12 text-center space-y-2">
        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <Mail className="w-6 h-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">{msg}</p>
      </CardContent>
    </Card>
  );
}

function EmailRow({ email }: { email: InboxEmailSummary }) {
  return (
    <Link
      to="/platform/inbox/$id"
      params={{ id: email.id }}
      className={cn(
        'block rounded-lg border px-3 py-2.5 hover:bg-accent/40 transition-colors',
        !email.is_read && 'bg-primary/5 border-primary/20',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="pt-0.5 shrink-0">
          {email.is_read ? (
            <MailOpen className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Mail className="w-4 h-4 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 justify-between">
            <span className={cn('text-sm truncate', !email.is_read && 'font-semibold')}>
              {email.from}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">
              {formatDate(email.received_at)}
            </span>
          </div>
          <div className="text-sm truncate">{email.subject || '(konu yok)'}</div>
          {email.preview && (
            <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {email.preview}
            </div>
          )}
          {email.attachments.length > 0 && (
            <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> {email.attachments.length} ek
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
